import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Stars as StarsIcon,
  Redeem as RedeemIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface InvoiceLoyaltyPointsProps {
  customerId?: string;
  customerName?: string;
  invoiceTotal: number;
  enabled?: boolean;
  pageId?: string; // معرف صفحة الفاتورة للحصول على إعدادات OTP
  onToggle?: (enabled: boolean) => void;
  onPointsRedeem?: (points: number, discountAmount: number) => void;
}

interface LoyaltyData {
  currentPoints: number;
  pointValue: number; // قيمة النقطة الواحدة بالريال
}

const InvoiceLoyaltyPoints: React.FC<InvoiceLoyaltyPointsProps> = ({
  customerId,
  customerName,
  invoiceTotal,
  enabled = true,
  pageId,
  onToggle,
  onPointsRedeem
}) => {
  const [isEnabled, setIsEnabled] = useState(enabled);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loyaltyData, setLoyaltyData] = useState<LoyaltyData | null>(null);
  const [pointsToRedeem, setPointsToRedeem] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  
  // حالات OTP
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // جلب إعدادات OTP من صفحة الفاتورة
  useEffect(() => {
    const fetchOtpSettings = async () => {
      if (!pageId) {
        console.log('⚠️ pageId غير موجود');
        return;
      }
      
      try {
        console.log('🔍 جلب إعدادات OTP لصفحة:', pageId);
        const response = await axiosInstance.get(`/invoices/pages/${pageId}`);
        console.log('📄 استجابة صفحة الفاتورة:', response.data);
        
        // البيانات قد تكون في response.data.data.page أو response.data.data
        const pageData = response.data.data?.page || response.data.data;
        console.log('📄 بيانات الصفحة:', pageData);
        
        const otpSettings = pageData?.loyaltyOtpSettings;
        console.log('⚙️ إعدادات OTP:', otpSettings);
        const isEnabled = otpSettings?.enabled || false;
        console.log('✅ OTP مفعّل؟', isEnabled);
        setOtpEnabled(isEnabled);
      } catch (err) {
        console.error('❌ خطأ في جلب إعدادات OTP:', err);
      }
    };
    
    fetchOtpSettings();
  }, [pageId]);

  // جلب بيانات نقاط الولاء للعميل
  useEffect(() => {
    const fetchLoyaltyData = async () => {
      if (!customerId || !isEnabled) {
        setLoyaltyData(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // جلب النقاط الحالية للعميل والبرنامج النشط
        const [balanceResponse, programResponse] = await Promise.all([
          axiosInstance.get(`/customers/${customerId}/loyalty-balance`),
          axiosInstance.get('/customers/active-loyalty-program')
        ]);
        
        console.log('استجابة API نقاط الولاء:', balanceResponse.data);
        console.log('استجابة API البرنامج النشط:', programResponse.data);
        
        // الحصول على قيمة النقطة من البرنامج النشط
        const pointValue = programResponse.data.data?.riyalPerPoint || 0.1;
        
        setLoyaltyData({
          currentPoints: balanceResponse.data.data?.balance || 0,
          pointValue: pointValue
        });
      } catch (err: any) {
        console.error('خطأ في جلب بيانات نقاط الولاء:', err);
        setError('فشل في تحميل بيانات نقاط الولاء');
      } finally {
        setLoading(false);
      }
    };

    fetchLoyaltyData();
  }, [customerId, invoiceTotal, isEnabled]);

  // معالجة تغيير حالة التفعيل
  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    setIsEnabled(newEnabled);
    if (onToggle) {
      onToggle(newEnabled);
    }
    // إعادة تعيين النقاط عند إلغاء التفعيل
    if (!newEnabled) {
      setPointsToRedeem(0);
      setDiscountAmount(0);
    }
  };

  // معالجة تغيير عدد النقاط المراد استبدالها
  const handlePointsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const points = parseInt(event.target.value) || 0;
    const maxPoints = loyaltyData?.currentPoints || 0;
    
    // التأكد من عدم تجاوز النقاط المتاحة
    const validPoints = Math.min(Math.max(0, points), maxPoints);
    setPointsToRedeem(validPoints);
    
    // حساب قيمة الخصم
    const discount = validPoints * (loyaltyData?.pointValue || 1);
    setDiscountAmount(discount);
  };

  // إرسال OTP
  const handleSendOtp = async () => {
    if (!customerId || !pageId) return;
    
    try {
      setSendingOtp(true);
      setError(null);
      
      const response = await axiosInstance.post('/customers/loyalty/send-otp', {
        customerId,
        pageId
      });
      
      if (response.data.success) {
        setOtpSent(true);
        // ✅ لا نحفظ OTP في Frontend - الآن محفوظ بشكل آمن في قاعدة البيانات
        alert('تم إرسال رمز التحقق إلى جوال العميل');
      }
    } catch (err: any) {
      console.error('خطأ في إرسال OTP:', err);
      setError(err.response?.data?.message || 'فشل في إرسال رمز التحقق');
    } finally {
      setSendingOtp(false);
    }
  };
  
  // التحقق من OTP من الخادم
  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError('يرجى إدخال رمز التحقق المكون من 6 أرقام');
      return;
    }
    
    try {
      setVerifyingOtp(true);
      setError(null);
      
      // التحقق من OTP من قاعدة البيانات
      const response = await axiosInstance.post('/customers/loyalty/verify-otp', {
        customerId,
        otp: otpCode
      });
      
      if (response.data.success) {
        setOtpVerified(true);
        alert('تم التحقق بنجاح! يمكنك الآن تطبيق الخصم');
      }
    } catch (err: any) {
      console.error('خطأ في التحقق من OTP:', err);
      setError(err.response?.data?.message || 'رمز التحقق غير صحيح');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // تطبيق الخصم (بدون خصم النقاط فعلياً - سيتم الخصم عند حفظ الفاتورة)
  const handleApplyDiscount = () => {
    if (!customerId || pointsToRedeem <= 0) return;
    
    // إذا كان OTP مفعل، يجب التحقق أولاً
    if (otpEnabled && !otpVerified) {
      setError('يجب التحقق من رمز OTP أولاً');
      return;
    }

    // فقط تطبيق الخصم على الفاتورة
    // النقاط ستُخصم فعلياً عند حفظ الفاتورة
    if (onPointsRedeem) {
      onPointsRedeem(pointsToRedeem, discountAmount);
    }

    // عرض رسالة تنبيه
    alert(`سيتم خصم ${pointsToRedeem} نقطة (${discountAmount} ريال) عند حفظ الفاتورة`);
    
    // إعادة تعيين حالة OTP
    setOtpSent(false);
    setOtpCode('');
    setOtpVerified(false);
  };

  // استخدام كل النقاط المتاحة
  const handleUseAllPoints = () => {
    if (loyaltyData) {
      const maxPoints = loyaltyData.currentPoints;
      setPointsToRedeem(maxPoints);
      const discount = maxPoints * loyaltyData.pointValue;
      setDiscountAmount(discount);
    }
  };

  // إذا لم يكن هناك عميل محدد
  if (!customerId) {
    return (
      <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <StarsIcon color="primary" />
              <Typography variant="h6">نقاط الولاء</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={handleToggle}
                  color="primary"
                  disabled
                />
              }
              label="تفعيل"
            />
          </Box>
          <Alert severity="info">
            يرجى اختيار عميل لعرض نقاط الولاء
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <StarsIcon color="primary" />
            <Typography variant="h6">نقاط الولاء</Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={handleToggle}
                color="primary"
              />
            }
            label="تفعيل"
          />
        </Box>

        {!isEnabled && (
          <Alert severity="info">
            نقاط الولاء غير مفعلة لهذه الفاتورة
          </Alert>
        )}

        {isEnabled && loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={40} />
          </Box>
        )}

        {isEnabled && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isEnabled && !loading && !error && loyaltyData && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                العميل: {customerName}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {/* عرض النقاط المتاحة */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    textAlign: 'center',
                    bgcolor: 'background.paper'
                  }}
                >
                  <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.dark', mb: 1 }} />
                  <Typography variant="h4" color="primary.dark" fontWeight="bold" sx={{ fontSize: '2.5rem' }}>
                    {loyaltyData.currentPoints}
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight="medium">
                    النقاط المتاحة للاستبدال
                  </Typography>
                  <Typography variant="caption" color="text.primary" sx={{ opacity: 0.8 }}>
                    (كل نقطة = {loyaltyData.pointValue} ريال)
                  </Typography>
                </Box>
              </Grid>

              {/* حقل إدخال عدد النقاط */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="عدد النقاط المراد استبدالها"
                  value={pointsToRedeem}
                  onChange={handlePointsChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RedeemIcon color="primary" />
                      </InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      max: loyaltyData.currentPoints
                    }
                  }}
                  helperText={`الحد الأقصى: ${loyaltyData.currentPoints} نقطة`}
                />
              </Grid>

              {/* زر استخدام كل النقاط */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={handleUseAllPoints}
                  disabled={loyaltyData.currentPoints === 0}
                >
                  استخدام كل النقاط المتاحة
                </Button>
              </Grid>

              {/* عرض قيمة الخصم */}
              {pointsToRedeem > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      border: '2px solid',
                      borderColor: 'success.main',
                      borderRadius: 1,
                      textAlign: 'center',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Typography variant="h5" color="success.dark" fontWeight="bold">
                      {discountAmount.toFixed(2)} ريال
                    </Typography>
                    <Typography variant="body2" color="text.primary" fontWeight="medium">
                      قيمة الخصم من النقاط
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* قسم OTP - يظهر فقط إذا كان مفعلاً */}
              {(() => {
                console.log('🔍 فحص شروط عرض OTP:', {
                  otpEnabled,
                  pointsToRedeem,
                  otpVerified,
                  shouldShow: otpEnabled && pointsToRedeem > 0 && !otpVerified
                });
                return null;
              })()}
              {otpEnabled && pointsToRedeem > 0 && !otpVerified && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      تأكيد استبدال النقاط
                    </Typography>
                  </Grid>
                  
                  {!otpSent ? (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        onClick={handleSendOtp}
                        disabled={sendingOtp}
                      >
                        {sendingOtp ? <CircularProgress size={24} /> : 'إرسال رمز التحقق'}
                      </Button>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="رمز التحقق (OTP)"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value)}
                          inputProps={{ maxLength: 6 }}
                          helperText="أدخل الرمز المكون من 6 أرقام المرسل إلى جوال العميل"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="secondary"
                          onClick={handleVerifyOtp}
                          disabled={verifyingOtp || otpCode.length !== 6}
                        >
                          {verifyingOtp ? <CircularProgress size={24} /> : 'تحقق من الرمز'}
                        </Button>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="text"
                          size="small"
                          onClick={handleSendOtp}
                          disabled={sendingOtp}
                        >
                          إعادة إرسال الرمز
                        </Button>
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* زر تطبيق الخصم */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleApplyDiscount}
                  disabled={pointsToRedeem === 0 || (otpEnabled && !otpVerified)}
                  startIcon={<RedeemIcon />}
                >
                  تطبيق الخصم
                </Button>
                {otpEnabled && !otpVerified && pointsToRedeem > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    يجب التحقق من رمز OTP أولاً
                  </Typography>
                )}
              </Grid>
            </Grid>

            {loyaltyData.currentPoints === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                لا توجد نقاط متاحة للاستبدال حالياً
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoiceLoyaltyPoints;